import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import Select from '../../components/ui/Select';
import FilterPanel from '../../components/ui/FilterPanel';
import BulkActionBar from '../../components/ui/BulkActionBar';
import Header from '../../components/ui/Header';

// Import components
import RuleCard from './components/RuleCard';
import RuleForm from './components/RuleForm';
import RuleTestModal from './components/RuleTestModal';
import RuleImportExport from './components/RuleImportExport';
import RuleStats from './components/RuleStats';

const MatchingRulesPage = () => {
  const navigate = useNavigate();
  const { user, currentOrgId } = useAuth();
  
  // State management
  const [rules, setRules] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedRules, setSelectedRules] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    patternType: '',
    vendor: '',
    confidenceRange: ''
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  
  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [testingRule, setTestingRule] = useState(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load real data from Supabase (vendors + rules)
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id || !currentOrgId) return;

      setIsLoading(true);
      try {
        // 1) Vendors
        const { data: vendorRows, error: vendorsErr } = await supabase
          .from('vendors')
          .select('id, display_name, account_ref, active')
          .eq('org_id', currentOrgId)
          .eq('active', true)
          .order('display_name');

        if (vendorsErr) throw vendorsErr;

        const vendorsMapped = (vendorRows || []).map(v => ({
          id: v?.id,
          name: v?.display_name || '<no vendor name>',
          code: v?.account_ref || '<no account ref>'
        }));

        // Create a lookup by account_ref for mapping rules -> vendor
        const vendorsByCode = Object.fromEntries(
          vendorsMapped.map(v => [v.code, v])
        );

        // 2) Rules
        const { data: ruleRows, error: rulesErr } = await supabase
          .from('matching_rules')
          .select('id, pattern, account_ref, enabled, created_at, updated_at, created_by, confidence_threshold, priority, pattern_type, case_sensitive, exact_match, patterns')
          .eq('org_id', currentOrgId)
          .order('created_at', { ascending: false });

        if (rulesErr) throw rulesErr;

        // Helper: determine pattern type
        const guessPatternType = (pattern) => {
          if (!pattern) return 'fuzzy';
          // If contains regex metacharacters, assume regex
          return /[\\^$.*+?()[\]{}|]/.test(pattern) ? 'regex' : 'fuzzy';
        };

        const mappedRules = (ruleRows || []).map(r => {
          const vendor = r?.account_ref ? vendorsByCode[r.account_ref] : null;
          const vendorName = vendor?.name || `<unknown vendor: ${r?.account_ref || 'N/A'}>`;
          const fromDbPatterns = Array.isArray(r?.patterns) ? r.patterns : null;
          const pattArray = fromDbPatterns && fromDbPatterns.length > 0
            ? fromDbPatterns.map(p => String(p))
            : [r?.pattern || '<placeholder: empty pattern>'];
          return {
            id: r?.id,
            name: (r?.pattern || pattArray?.[0] || '<placeholder: no pattern>'),
            patterns: pattArray,
            patternType: r?.pattern_type || guessPatternType(r?.pattern),
            vendorId: vendor?.id || null,
            vendorName,
            _vendorIsPlaceholder: !vendor,
            confidenceThreshold: r?.confidence_threshold != null ? Number(r.confidence_threshold) : 0.8,
            _confidenceIsPlaceholder: r?.confidence_threshold == null,
            priority: r?.priority != null ? Number(r.priority) : 1,
            _priorityIsPlaceholder: r?.priority == null,
            isActive: !!r?.enabled,
            caseSensitive: !!r?.case_sensitive,
            exactMatch: !!r?.exact_match,
            matchCount: 0, // placeholder
            successRate: 0, // placeholder
            createdAt: r?.created_at ? new Date(r.created_at) : null,
            lastUsed: r?.updated_at ? new Date(r.updated_at) : null
          };
        });

        setVendors(vendorsMapped);
        setRules(mappedRules);
      } catch (err) {
        console.error('Failed to load matching rules:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user?.id, currentOrgId]);

  // Filter schema for FilterPanel
  const filterSchema = [
    {
      key: 'search',
      type: 'search',
      placeholder: 'Search rules, patterns, or vendors...'
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: '', label: 'All Status' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      clearable: true
    },
    {
      key: 'patternType',
      type: 'select',
      label: 'Pattern Type',
      options: [
        { value: '', label: 'All Types' },
        { value: 'fuzzy', label: 'Fuzzy Matching' },
        { value: 'regex', label: 'Regular Expression' },
        { value: 'exact', label: 'Exact Match' }
      ],
      clearable: true
    },
    {
      key: 'vendor',
      type: 'select',
      label: 'Vendor',
      options: [
        { value: '', label: 'All Vendors' },
        ...vendors?.map(vendor => ({
          value: vendor?.id,
          label: vendor?.name
        }))
      ],
      searchable: true,
      clearable: true
    },
    {
      key: 'confidenceRange',
      type: 'select',
      label: 'Confidence',
      options: [
        { value: '', label: 'All Levels' },
        { value: 'high', label: 'High (≥80%)' },
        { value: 'medium', label: 'Medium (60-79%)' },
        { value: 'low', label: 'Low (<60%)' }
      ],
      clearable: true
    }
  ];

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Rule Name' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'priority', label: 'Priority' },
    { value: 'matchCount', label: 'Match Count' },
    { value: 'successRate', label: 'Success Rate' },
    { value: 'createdAt', label: 'Created Date' },
    { value: 'lastUsed', label: 'Last Used' }
  ];

  // Filter and sort rules
  const filteredAndSortedRules = React.useMemo(() => {
    let filtered = rules?.filter(rule => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        const matchesSearch = 
          rule?.name?.toLowerCase()?.includes(searchTerm) ||
          rule?.vendorName?.toLowerCase()?.includes(searchTerm) ||
          rule?.patterns?.some(pattern => pattern?.toLowerCase()?.includes(searchTerm));
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters?.status) {
        if (filters?.status === 'active' && !rule?.isActive) return false;
        if (filters?.status === 'inactive' && rule?.isActive) return false;
      }

      // Pattern type filter
      if (filters?.patternType && rule?.patternType !== filters?.patternType) {
        return false;
      }

      // Vendor filter
      if (filters?.vendor && rule?.vendorId !== filters?.vendor) {
        return false;
      }

      // Confidence range filter
      if (filters?.confidenceRange) {
        const threshold = rule?.confidenceThreshold;
        if (filters?.confidenceRange === 'high' && threshold < 0.8) return false;
        if (filters?.confidenceRange === 'medium' && (threshold < 0.6 || threshold >= 0.8)) return false;
        if (filters?.confidenceRange === 'low' && threshold >= 0.6) return false;
      }

      return true;
    });

    // Sort rules
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      // Handle special cases
      if (sortBy === 'vendor') {
        aValue = a?.vendorName;
        bValue = b?.vendorName;
      }

      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [rules, filters, sortBy, sortOrder]);

  // Bulk action handlers
  const handleSelectAll = () => {
    setSelectedRules(filteredAndSortedRules?.map(rule => rule?.id));
  };

  const handleClearSelection = () => {
    setSelectedRules([]);
  };

  const handleRuleSelection = (ruleId, isSelected) => {
    if (isSelected) {
      setSelectedRules(prev => [...prev, ruleId]);
    } else {
      setSelectedRules(prev => prev?.filter(id => id !== ruleId));
    }
  };

  // Bulk actions configuration
  const bulkActions = [
    {
      label: 'Activate',
      icon: 'Play',
      onClick: () => handleBulkStatusChange(true),
      primary: true
    },
    {
      label: 'Deactivate',
      icon: 'Pause',
      onClick: () => handleBulkStatusChange(false)
    },
    {
      label: 'Delete',
      icon: 'Trash2',
      onClick: () => handleBulkDelete(),
      destructive: true,
      requiresConfirmation: true,
      confirmTitle: 'Delete Matching Rules',
      confirmMessage: `Are you sure you want to delete ${selectedRules.length} matching rule${selectedRules.length !== 1 ? 's' : ''}? This action cannot be undone and may affect future invoice processing.`
    }
  ];

  // Action handlers
  const handleBulkStatusChange = async (isActive) => {
    setIsProcessing(true);
    try {
      if (selectedRules?.length > 0) {
        const { error } = await supabase
          .from('matching_rules')
          .update({ enabled: isActive })
          .in('id', selectedRules)
          .eq('org_id', currentOrgId);
        if (error) throw error;
      }

      setRules(prev => prev?.map(rule =>
        selectedRules?.includes(rule?.id)
          ? { ...rule, isActive }
          : rule
      ));
    } catch (err) {
      console.error('Bulk status update failed:', err);
    } finally {
      setSelectedRules([]);
      setIsProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    setIsProcessing(true);
    try {
      if (selectedRules?.length > 0) {
        const { error } = await supabase
          .from('matching_rules')
          .delete()
          .in('id', selectedRules)
          .eq('org_id', currentOrgId);
        if (error) throw error;
      }
      setRules(prev => prev?.filter(rule => !selectedRules?.includes(rule?.id)));
    } catch (err) {
      console.error('Bulk delete failed:', err);
    } finally {
      setSelectedRules([]);
      setIsProcessing(false);
    }
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setIsFormModalOpen(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setIsFormModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this matching rule? This action cannot be undone.')) {
      return;
    }

    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('matching_rules')
        .delete()
        .eq('id', ruleId)
        .eq('org_id', currentOrgId);
      if (error) throw error;
      setRules(prev => prev?.filter(rule => rule?.id !== ruleId));
    } catch (err) {
      console.error('Delete rule failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleRuleStatus = async (ruleId) => {
    const target = rules?.find(r => r?.id === ruleId);
    if (!target) return;
    const next = !target.isActive;
    try {
      const { error } = await supabase
        .from('matching_rules')
        .update({ enabled: next })
        .eq('id', ruleId)
        .eq('org_id', currentOrgId);
      if (error) throw error;
      setRules(prev => prev?.map(rule =>
        rule?.id === ruleId ? { ...rule, isActive: next } : rule
      ));
    } catch (err) {
      console.error('Toggle rule status failed:', err);
    }
  };

  const handleTestRule = (rule) => {
    setTestingRule(rule);
    setIsTestModalOpen(true);
  };

  const handleSaveRule = async (ruleData) => {
    setIsProcessing(true);
    try {
      // Map UI -> DB
      const primaryPattern = (ruleData?.patterns || [])?.find(p => p?.trim()) || null;
      const vendor = vendors?.find(v => v?.id === ruleData?.vendorId);
      const accountRef = vendor?.code || null;

      if (!primaryPattern || !accountRef) {
        throw new Error('Pattern and vendor (account ref) are required');
      }

      if (editingRule) {
        const { error } = await supabase
          .from('matching_rules')
          .update({
            pattern: primaryPattern,
            account_ref: accountRef,
            enabled: !!ruleData?.isActive,
            org_id: currentOrgId,
            confidence_threshold: ruleData?.confidenceThreshold ?? null,
            priority: ruleData?.priority ?? null,
            pattern_type: ruleData?.patternType ?? null,
            case_sensitive: !!ruleData?.caseSensitive,
            exact_match: !!ruleData?.exactMatch,
            patterns: (ruleData?.patterns && ruleData.patterns.length > 0) ? ruleData.patterns : null
          })
          .eq('id', editingRule?.id)
          .eq('org_id', currentOrgId);
        if (error) throw error;

        // Update local
        setRules(prev => prev?.map(rule =>
          rule?.id === editingRule?.id
            ? {
                ...rule,
                name: primaryPattern,
                patterns: [primaryPattern],
                vendorId: vendor?.id || null,
                vendorName: vendor?.name || `<unknown vendor: ${accountRef}>`,
                _vendorIsPlaceholder: !vendor,
                isActive: !!ruleData?.isActive,
                lastUsed: new Date()
              }
            : rule
        ));
      } else {
        const { data: inserted, error } = await supabase
          .from('matching_rules')
          .insert({
            pattern: primaryPattern,
            account_ref: accountRef,
            enabled: !!ruleData?.isActive,
            created_by: user?.id,
            org_id: currentOrgId,
            confidence_threshold: ruleData?.confidenceThreshold ?? 0.8,
            priority: ruleData?.priority ?? 1,
            pattern_type: ruleData?.patternType ?? null,
            case_sensitive: !!ruleData?.caseSensitive,
            exact_match: !!ruleData?.exactMatch,
            patterns: (ruleData?.patterns && ruleData.patterns.length > 0) ? ruleData.patterns : [primaryPattern]
          })
          .select('id, pattern, account_ref, enabled, created_at, updated_at, confidence_threshold, priority, pattern_type, case_sensitive, exact_match, patterns')
          .single();
        if (error) throw error;

        const newRule = {
          id: inserted?.id,
          name: inserted?.pattern,
          patterns: [inserted?.pattern],
          patternType: inserted?.pattern_type || (/[\\^$.*+?()[\]{}|]/.test(inserted?.pattern) ? 'regex' : 'fuzzy'),
          vendorId: vendor?.id || null,
          vendorName: vendor?.name || `<unknown vendor: ${inserted?.account_ref || 'N/A'}>`,
          _vendorIsPlaceholder: !vendor,
          confidenceThreshold: inserted?.confidence_threshold != null ? Number(inserted.confidence_threshold) : 0.8,
          _confidenceIsPlaceholder: inserted?.confidence_threshold == null,
          priority: inserted?.priority != null ? Number(inserted.priority) : 1,
          _priorityIsPlaceholder: inserted?.priority == null,
          isActive: !!inserted?.enabled,
          caseSensitive: !!inserted?.case_sensitive,
          exactMatch: !!inserted?.exact_match,
          matchCount: 0,
          successRate: 0,
          createdAt: inserted?.created_at ? new Date(inserted.created_at) : new Date(),
          lastUsed: inserted?.updated_at ? new Date(inserted.updated_at) : null
        };
        setRules(prev => [newRule, ...prev]);
      }

      setIsFormModalOpen(false);
      setEditingRule(null);
    } catch (err) {
      console.error('Save rule failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImportRules = async (importedRules) => {
    setIsProcessing(true);
    try {
      if (!Array.isArray(importedRules) || importedRules.length === 0) return;

      // Map import -> DB rows (first pattern only)
      const rows = importedRules
        .map(r => ({
          pattern: (r?.patterns || [])?.find(p => p?.trim()) || r?.name || null,
          vendor: vendors?.find(v => v?.id === r?.vendorId) || null,
          enabled: r?.isActive ?? true
        }))
        .filter(x => x?.pattern && x?.vendor)
        .map(x => ({
          pattern: x.pattern,
          account_ref: x.vendor.code,
          enabled: !!x.enabled,
          created_by: user?.id,
          org_id: currentOrgId,
          patterns: [x.pattern],
          confidence_threshold: 0.8,
          priority: 1,
          pattern_type: /[\\^$.*+?()[\]{}|]/.test(x.pattern) ? 'regex' : 'fuzzy',
          case_sensitive: false,
          exact_match: false
        }));

      if (rows.length > 0) {
        const { data, error } = await supabase
          .from('matching_rules')
          .insert(rows)
          .select('id, pattern, account_ref, enabled, created_at, updated_at, confidence_threshold, priority, pattern_type, case_sensitive, exact_match, patterns');
        if (error) throw error;

        // Merge into local state
        const vendorsByCode = Object.fromEntries(vendors.map(v => [v.code, v]));
        const mapped = (data || []).map(r => {
          const vendor = vendorsByCode[r.account_ref];
          return {
            id: r.id,
            name: r.pattern,
            patterns: Array.isArray(r?.patterns) && r.patterns.length > 0 ? r.patterns.map(p => String(p)) : [r.pattern],
            patternType: r?.pattern_type || (/[\\^$.*+?()[\]{}|]/.test(r.pattern) ? 'regex' : 'fuzzy'),
            vendorId: vendor?.id || null,
            vendorName: vendor?.name || `<unknown vendor: ${r.account_ref || 'N/A'}>`,
            _vendorIsPlaceholder: !vendor,
            confidenceThreshold: r?.confidence_threshold != null ? Number(r.confidence_threshold) : 0.8,
            _confidenceIsPlaceholder: r?.confidence_threshold == null,
            priority: r?.priority != null ? Number(r.priority) : 1,
            _priorityIsPlaceholder: r?.priority == null,
            isActive: !!r.enabled,
            caseSensitive: !!r?.case_sensitive,
            exactMatch: !!r?.exact_match,
            matchCount: 0,
            successRate: 0,
            createdAt: r?.created_at ? new Date(r.created_at) : new Date(),
            lastUsed: r?.updated_at ? new Date(r.updated_at) : null
          };
        });
        setRules(prev => [...mapped, ...prev]);
      }
    } catch (err) {
      console.error('Import rules failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportRules = (exportData) => {
    // Export handled by component
    console.log('Rules exported:', exportData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center space-y-4">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Loading matching rules...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Matching Rules</h1>
              <p className="text-muted-foreground mt-2">
                Automate vendor recognition with intelligent pattern matching
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <RuleImportExport
                rules={rules}
                onImport={handleImportRules}
                onExport={handleExportRules}
              />
              
              <Button
                variant="default"
                onClick={handleCreateRule}
                iconName="Plus"
                iconPosition="left"
              >
                Create Rule
              </Button>
            </div>
          </div>

          {/* Statistics */}
          <RuleStats rules={rules} />

          {/* Filters */}
          <FilterPanel
            filters={filters}
            onFiltersChange={setFilters}
            schema={filterSchema}
            isCollapsed={isFiltersCollapsed}
            onToggleCollapse={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
          />

          {/* Bulk Actions */}
          <BulkActionBar
            selectedCount={selectedRules?.length}
            totalCount={filteredAndSortedRules?.length}
            actions={bulkActions}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            position="sticky"
          />

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                {filteredAndSortedRules?.length} of {rules?.length} rules
              </span>
              
              {filters?.search && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-primary/10 rounded-full">
                  <Icon name="Search" size={14} className="text-primary" />
                  <span className="text-sm text-primary">"{filters?.search}"</span>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                    className="text-primary hover:text-primary/80"
                  >
                    <Icon name="X" size={14} />
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex items-center border border-border rounded-md">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`
                    px-3 py-1.5 text-sm transition-smooth
                    ${viewMode === 'cards' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name="LayoutGrid" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`
                    px-3 py-1.5 text-sm transition-smooth
                    ${viewMode === 'table' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>

              {/* Sort Controls */}
              <Select
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                className="w-40"
              />
              
              <button
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="p-2 border border-border rounded-md hover:bg-muted transition-smooth"
                title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
              >
                <Icon name={sortOrder === 'asc' ? "ArrowUp" : "ArrowDown"} size={16} />
              </button>
            </div>
          </div>

          {/* Rules Display */}
          {filteredAndSortedRules?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {rules?.length === 0 ? 'No matching rules yet' : 'No rules match your filters'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {rules?.length === 0 
                  ? 'Create your first matching rule to automate vendor recognition'
                  : 'Try adjusting your search criteria or filters'
                }
              </p>
              {rules?.length === 0 && (
                <Button
                  variant="default"
                  onClick={handleCreateRule}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Create First Rule
                </Button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'cards' ? 'space-y-4' : ''}>
              {viewMode === 'cards' ? (
                // Card View
                (filteredAndSortedRules?.map(rule => (
                  <div key={rule?.id} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedRules?.includes(rule?.id)}
                      onChange={(e) => handleRuleSelection(rule?.id, e?.target?.checked)}
                      className="mt-6 w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <RuleCard
                      rule={rule}
                      onEdit={handleEditRule}
                      onDelete={handleDeleteRule}
                      onToggleStatus={handleToggleRuleStatus}
                      onTest={handleTestRule}
                      className="flex-1"
                    />
                  </div>
                )))
              ) : (
                // Table View
                (<div className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="w-12 p-4">
                            <input
                              type="checkbox"
                              checked={selectedRules?.length === filteredAndSortedRules?.length && filteredAndSortedRules?.length > 0}
                              onChange={(e) => e?.target?.checked ? handleSelectAll() : handleClearSelection()}
                              className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                            />
                          </th>
                          <th className="text-left p-4 font-medium text-foreground">Rule Name</th>
                            <th className="text-left p-4 font-medium text-foreground">Vendor</th>
                          <th className="text-left p-4 font-medium text-foreground">Type</th>
                          <th className="text-left p-4 font-medium text-foreground">Patterns</th>
                          <th className="text-left p-4 font-medium text-foreground">Confidence</th>
                          <th className="text-left p-4 font-medium text-foreground">Matches</th>
                          <th className="text-left p-4 font-medium text-foreground">Success Rate</th>
                          <th className="text-left p-4 font-medium text-foreground">Status</th>
                          <th className="text-right p-4 font-medium text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedRules?.map(rule => (
                          <tr key={rule?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                            <td className="p-4">
                              <input
                                type="checkbox"
                                checked={selectedRules?.includes(rule?.id)}
                                onChange={(e) => handleRuleSelection(rule?.id, e?.target?.checked)}
                                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                              />
                            </td>
                            <td className="p-4">
                              <div className="font-medium text-foreground">{rule?.name}</div>
                              <div className="text-sm text-muted-foreground">Priority: {rule?.priority}</div>
                            </td>
                            <td className="p-4">
                              <span className={rule?._vendorIsPlaceholder ? 'text-muted-foreground italic' : 'text-foreground'}>
                                {rule?.vendorName}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-muted rounded text-xs font-medium text-foreground capitalize">
                                {rule?.patternType}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {rule?.patterns?.length} pattern{rule?.patterns?.length !== 1 ? 's' : ''}
                            </td>
                            <td className="p-4">
                              <span className={`
                                text-sm font-medium
                                ${rule?.confidenceThreshold >= 0.8 ? 'text-success' : 
                                  rule?.confidenceThreshold >= 0.6 ? 'text-warning' : 'text-error'}
                              `}>
                                {Math.round(rule?.confidenceThreshold * 100)}%
                              </span>
                              {rule?._confidenceIsPlaceholder && (
                                <span className="ml-2 text-xs text-muted-foreground italic">&lt;placeholder&gt;</span>
                              )}
                            </td>
                            <td className="p-4 text-foreground">{rule?.matchCount?.toLocaleString()}</td>
                            <td className="p-4">
                              {rule?.successRate == null ? (
                                <span className="text-sm text-muted-foreground italic">&lt;placeholder&gt;</span>
                              ) : (
                                <span className="text-sm font-medium text-success">
                                  {Math.round(rule?.successRate * 100)}%
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              <button
                                onClick={() => handleToggleRuleStatus(rule?.id)}
                                className={`
                                  flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-smooth
                                  ${rule?.isActive 
                                    ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                                  }
                                `}
                              >
                                <div className={`w-2 h-2 rounded-full ${rule?.isActive ? 'bg-success' : 'bg-muted-foreground'}`} />
                                <span>{rule?.isActive ? 'Active' : 'Inactive'}</span>
                              </button>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTestRule(rule)}
                                  iconName="Play"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditRule(rule)}
                                  iconName="Edit"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteRule(rule?.id)}
                                  iconName="Trash2"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>)
              )}
            </div>
          )}
        </div>
      </div>
      {/* Modals */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsFormModalOpen(false)}
          />
          <div className="relative max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <RuleForm
              rule={editingRule}
              vendors={vendors}
              onSave={handleSaveRule}
              onCancel={() => setIsFormModalOpen(false)}
            />
          </div>
        </div>
      )}
      <RuleTestModal
        rule={testingRule}
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
      />
    </div>
  );
};

export default MatchingRulesPage;
